// ============================================================
// ColoniePrintUtils.js
// Shared pricing constants, labels, and receipt printer
// ============================================================

export const P = {
  cotisation:        375,
  season3Days:       265,
  monthly3Days:      265,
  latePickup3to330:   60,
  latePickup3to4:    120,
  trial:              25,   // trial package
  nerdOnly:          200,   // standalone, outside camp
  nerdInCamp:        130,
  nerdOutCamp:       200,
};

export const PKG_LABEL = {
  Cotisation:   'Cotisation — Full Season 2026',
  Season3Days:  '3 Days/Week — Full Season 2026',
  Monthly3Days: 'Monthly',
  NerdOnly:     'Nerd Package — Standalone (Outside Camp)',
  Trial:        'Trial Package',
};

export const PKG_SHORT = {
  Cotisation:   'Cotisation',
  Season3Days:  '3Days/Season',
  Monthly3Days: 'Monthly',
  NerdOnly:     'Nerd Only',
  Trial:        'Trial',
};

export function computeFees({ packageType, transportFee = 0, latePickup, hasNerd, nerdType }) {
  const pkg = { Cotisation: P.cotisation, Season3Days: P.season3Days, Monthly3Days: P.monthly3Days, NerdOnly: P.nerdOnly, Trial: P.trial }[packageType] || 0;
  const tf  = parseFloat(transportFee || 0);
  const lp  = latePickup === '3to330' ? P.latePickup3to330 : latePickup === '3to4' ? P.latePickup3to4 : 0;
  // NerdOnly already includes nerd — don't double-charge
  const nf  = (hasNerd && packageType !== 'NerdOnly') ? (nerdType === 'outcamp' ? P.nerdOutCamp : P.nerdInCamp) : 0;
  return { pkg, tf, lp, nf, total: pkg + tf + lp + nf };
}

// ── Receipt printer ──────────────────────────────────────────
// Always shows full current charge breakdown.
// reg = vw_ColonieRegistrations row (has PackageFee, TransportFeeAmt etc.)
// pmt = { receiptNumber, amount, balanceRemaining, paymentMethod, referenceNumber, notes }
// enrollment = fallback for first-payment case (fees object from API)
export function printColonieReceipt(reg, pmt, enrollment) {
  const pkgType      = reg.PackageType    || enrollment?.packageType    || enrollment?.PackageType    || '';
  const pkgFee       = parseFloat(reg.PackageFee      ?? enrollment?.fees?.pkgFee  ?? 0);
  const tFee         = parseFloat(reg.TransportFeeAmt ?? enrollment?.fees?.tf      ?? enrollment?.transportFee ?? 0);
  const lpFee        = parseFloat(reg.LatePickupFee   ?? enrollment?.fees?.lp      ?? 0);
  const nFee         = parseFloat(reg.NerdFee         ?? enrollment?.fees?.nf      ?? 0);
  const totalCharged = parseFloat(reg.TotalCharged    ?? enrollment?.fees?.total   ?? 0);
  const totalPaid    = parseFloat(reg.TotalPaid       || 0);
  const latePickup   = reg.LatePickup  || enrollment?.latePickup  || enrollment?.LatePickup  || '';
  const nerdType_    = reg.NerdType    || enrollment?.nerdType    || enrollment?.NerdType    || 'incamp';
  const monthsCharged = parseInt(reg.MonthsCharged || 1);

  const discount    = parseFloat(reg.Discount || 0);
  const bal  = Math.max(0, parseFloat(pmt.balanceRemaining ?? (totalCharged - totalPaid) ?? 0));
  const date = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

  let pkgLabel = PKG_LABEL[pkgType] || pkgType;
  if (pkgType === 'Monthly3Days')
    pkgLabel = `Monthly (${monthsCharged} month${monthsCharged > 1 ? 's' : ''})`;

  const html = `<!DOCTYPE html><html><head><title>Receipt ${pmt.receiptNumber}</title>
<style>
  @page { size: A5 portrait; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  html, body { width: 148mm; margin: 0; padding: 0; }
  @media print { body { margin: 0; } }
  body  { font-family: Arial, sans-serif; color: #111; font-size: 11px; padding: 10mm 12mm; }
  .top  { border-bottom: 2.5px solid #f97316; padding-bottom: 8px; margin-bottom: 10px; }
  .school { font-size: 15px; font-weight: bold; color: #1e3a5f; margin: 0; }
  .sub    { font-size: 10px; color: #f97316; margin: 2px 0 0; }
  .meta   { font-size: 8.5px; color: #888; margin-top: 4px; }
  .meta strong { color: #111; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 10px; }
  .cell { background: #fafafa; border: 1px solid #e5e7eb; border-radius: 4px; padding: 4px 7px; }
  .cell label { font-size: 7.5px; text-transform: uppercase; color: #aaa; display: block; margin-bottom: 1px; }
  .cell span  { font-size: 10px; font-weight: 600; }

  .meth { display: inline-block; background: #eff6ff; color: #1d4ed8; padding: 1px 6px; border-radius: 8px; font-size: 9.5px; font-weight: 700; }
  .sec  { font-size: 7.5px; text-transform: uppercase; color: #bbb; letter-spacing: .5px; border-bottom: .5px solid #eee; padding-bottom: 2px; margin: 8px 0 4px; }
  table { width: 100%; border-collapse: collapse; }
  td    { padding: 3.5px 3px; font-size: 10.5px; border-bottom: .5px solid #f5f5f5; color: #333; }
  td:last-child { text-align: right; white-space: nowrap; }
  .tot  td { font-size: 12px; font-weight: bold; background: #fff7ed; color: #ea580c; border-top: 2px solid #f97316; border-bottom: none; padding: 5px 3px; }
  .prev td { color: #999; }
  .paid td { font-size: 12px; font-weight: bold; color: #16a34a; padding: 5px 3px; border-bottom: none; }
  .bal  td { font-size: 12px; font-weight: 600; color: ${bal > 0 ? '#dc2626' : '#16a34a'}; border-bottom: none; }
  .footer { margin-top: 10px; text-align: center; font-size: 8.5px; color: #ccc; border-top: .5px solid #eee; padding-top: 5px; }
</style></head><body>
<div class="top">
  <p class="school">Collège des Apôtres</p>
  <p class="sub">Colonie Des Rêves — Summer Camp 2026</p>
  <p class="meta">Receipt: <strong>${pmt.receiptNumber}</strong>&nbsp;&nbsp;|&nbsp;&nbsp;Date: <strong>${date}</strong></p>
</div>
<div class="grid">
  <div class="cell"><label>Child</label><span>${reg.ChildName}</span></div>
  <div class="cell"><label>Class</label><span>${reg.Class || '—'}${reg.Branch ? ' — ' + reg.Branch : ''}</span></div>
  <div class="cell"><label>Method</label><span class="meth">${pmt.paymentMethod}</span></div>
  <div class="cell"><label>Mother</label><span>${reg.MotherName || '—'}</span></div>
  <div class="cell"><label>Father</label><span>${reg.FatherName || '—'}</span></div>
  <div class="cell"><label>Phone</label><span>${reg.MotherPhone || '—'}</span></div>
  ${pmt.referenceNumber ? `<div class="cell"><label>Ref / Check #</label><span>${pmt.referenceNumber}</span></div>` : ''}
  ${reg.FamilyID ? `<div class="cell"><label>School family</label><span>${reg.FamilyID}</span></div>` : ''}
</div>
<div class="cols">
  <div>
    <div class="sec">Charge breakdown</div>
    <table>
      <tr><td>${pkgLabel}</td><td>${pkgFee.toFixed(2)}</td></tr>
      ${tFee > 0  ? `<tr><td>Transportation</td><td>${tFee.toFixed(2)}</td></tr>` : ''}
      ${lpFee > 0 ? `<tr><td>Late Pickup ${latePickup === '3to330' ? '3:00–3:30' : '3:00–4:00'} PM</td><td>${lpFee.toFixed(2)}</td></tr>` : ''}
      ${nFee > 0  ? `<tr><td>Nerd — ${nerdType_ === 'outcamp' ? 'Outside Camp' : 'In Camp'}</td><td>${nFee.toFixed(2)}</td></tr>` : ''}
      ${discount > 0 ? `<tr style="color:#16a34a"><td>Discount</td><td style="text-align:right">-${discount.toFixed(2)}</td></tr>` : ''}
      <tr class="tot"><td>Total</td><td>${totalCharged.toFixed(2)}</td></tr>
    </table>
  </div>
  <div>
    <div class="sec">Payment</div>
    <table>
      <tr class="prev"><td>Previously paid</td><td>${(totalPaid - parseFloat(pmt.amount || 0)).toFixed(2)}</td></tr>
      <tr class="paid"><td>Paid today</td><td>${parseFloat(pmt.amount || 0).toFixed(2)}</td></tr>
      <tr class="bal"><td>${bal > 0 ? '⚠ Balance due' : '✓ Fully settled'}</td><td>${bal.toFixed(2)}</td></tr>
    </table>
    ${pmt.notes ? `<p style="margin-top:6px;font-size:8.5px;color:#666">Notes: ${pmt.notes}</p>` : ''}
  </div>
</div>
<div class="footer">Thank you — Collège des Apôtres × Colonie Des Rêves 2026</div>
</body></html>`;

  const windowPrint = window.open('', '', 'width=600,height=850');
  if (!windowPrint) { alert('Pop-up blocked — please allow pop-ups for this site.'); return; }
  windowPrint.document.write(html);
  windowPrint.document.close();
  windowPrint.focus();
  windowPrint.print();
  windowPrint.close();
}
